import { trackEvent } from "@/utils/analytics";

/**
 * One delegated listener records the clicks that matter for conversion, so
 * links anywhere on the site are covered without wiring each one:
 *
 * - WhatsApp links → `whatsapp_click` (components that track themselves carry `data-tracked`)
 * - email, phone and external links → `outbound_click`
 * - internal call-to-action links (`data-cta`) → `cta_click`
 * - opening a FAQ question → `faq_open`
 *
 * `location` is the nearest `data-track-location`, section id or landmark.
 */
function clickLocation(el: Element): string {
  const explicit = el.closest<HTMLElement>("[data-track-location]")?.dataset.trackLocation;
  if (explicit) return explicit;
  const section = el.closest("section");
  const id = section?.id || section?.getAttribute("aria-labelledby");
  if (id) return id;
  if (el.closest("header")) return "header";
  if (el.closest("footer")) return "footer";
  if (el.closest("[role='dialog'], dialog")) return "dialog";
  return "page";
}

function label(el: Element): string {
  return (el.getAttribute("aria-label") || el.textContent || "").replace(/\s+/g, " ").trim().slice(0, 80);
}

function outboundTarget(url: URL): string {
  const host = url.hostname.replace(/^www\./, "");
  if (host.includes("instagram.com")) return "instagram";
  if (host.includes("tiktok.com")) return "tiktok";
  if (host.includes("calendly.com")) return "calendly";
  if (
    (host.includes("google.") && url.pathname.includes("/maps")) ||
    host === "maps.app.goo.gl"
  ) {
    return "google_maps";
  }
  return host;
}

function onClick(event: MouseEvent) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const summary = target.closest("summary");
  if (summary) {
    const details = summary.parentElement;
    /* The click toggles it open afterwards: a closed <details> is being opened. */
    if (details instanceof HTMLDetailsElement && !details.open) {
      const question = summary.querySelector("h2, h3, h4")?.textContent ?? summary.textContent ?? "";
      trackEvent("faq_open", { question: question.trim().slice(0, 160), location: clickLocation(summary) });
    }
    return;
  }

  const anchor = target.closest("a");
  if (!anchor || anchor.hasAttribute("data-tracked")) return;
  const href = anchor.getAttribute("href") ?? "";
  if (!href || href.startsWith("#")) return;
  const location = clickLocation(anchor);

  if (href.startsWith("mailto:")) {
    trackEvent("outbound_click", { target: "email", location });
    return;
  }
  if (href.startsWith("tel:")) {
    trackEvent("outbound_click", { target: "phone", location });
    return;
  }

  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return;
  }

  if (url.hostname === "wa.me" || url.hostname.endsWith("whatsapp.com")) {
    trackEvent("whatsapp_click", { location });
    return;
  }
  if (url.origin !== window.location.origin) {
    trackEvent("outbound_click", { target: outboundTarget(url), location });
    return;
  }
  if (anchor.hasAttribute("data-cta")) {
    trackEvent("cta_click", { location, label: label(anchor), target: `${url.pathname}${url.hash}` });
  }
}

export function installClickTracking(): () => void {
  document.addEventListener("click", onClick, { capture: true });
  return () => document.removeEventListener("click", onClick, { capture: true });
}
